import { App, Modal, type ModalFuncProps } from 'antd';
import type { ReactNode } from 'react';
import { logger } from './logger';

/**
 * Type for modal instance from App.useApp()
 */
type ModalInstance = ReturnType<typeof App.useApp>['modal'];

/**
 * Common dialog types for the application
 */
export type DialogType = 'error' | 'info' | 'warning' | 'confirm';

/**
 * Base dialog configuration options
 */
export interface DialogOptions {
  title?: ReactNode;
  content?: ReactNode;
  okText?: string;
  cancelText?: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  closable?: boolean;
  centered?: boolean;
}

/**
 * Button types for confirm dialogs
 */
export type ConfirmButtonType =
  | 'primary'
  | 'default'
  | 'dashed'
  | 'link'
  | 'text';

/**
 * Configuration for confirm dialog with additional cancel handling
 */
export interface ConfirmDialogOptions extends DialogOptions {
  okType?: ConfirmButtonType;
  danger?: boolean;
}

/**
 * Default messages for each dialog type
 */
const defaultMessages = {
  error: {
    title: 'Error',
    content: 'An error has occurred. Please try again.',
  },
  info: {
    title: 'Information',
    content: 'Operation completed.',
  },
  warning: {
    title: 'Warning',
    content: 'Please review before proceeding.',
  },
  confirm: {
    title: 'Confirm',
    content: 'Are you sure you want to proceed?',
  },
  deleteConfirm: {
    title: 'Confirm Delete',
    content: 'Are you sure you want to delete this item?',
  },
  success: {
    title: 'Success',
    content: 'Operation completed successfully.',
  },
} as const;

/**
 * Default dialog configuration
 */
const defaultConfig: Partial<ModalFuncProps> = {
  centered: true,
  closable: true,
  maskClosable: false,
};

/**
 * Dialog service singleton
 * Stores the modal instance from App component to ensure proper theming
 * and context consumption (fixes Ant Design warning about static Modal methods)
 */
class DialogService {
  private static modal: ModalInstance | null = null;

  static setInstance(modalInstance: ModalInstance) {
    this.modal = modalInstance;
  }

  static getModal(): ModalInstance {
    if (!this.modal) {
      logger.warn(
        'Modal instance not initialized. Make sure App component is wrapped with AntApp and useDialogService is called.',
      );
      // Fallback to static Modal methods (will show warning but still work)
      return Modal as unknown as ModalInstance;
    }
    return this.modal;
  }
}

/**
 * Shows an error dialog for displaying error messages
 * @param options - Dialog configuration options
 * @returns Modal instance for programmatic control
 *
 * @example
 * showError({
 *   title: 'Error',
 *   content: 'Failed to save data. Please try again.',
 *   onOk: () => console.log('Error acknowledged'),
 * });
 */
export function showError(options: DialogOptions = {}) {
  const {
    title = defaultMessages.error.title,
    content = defaultMessages.error.content,
    okText = 'OK',
    onOk,
    onCancel,
    closable,
    centered,
  } = options;

  const modal = DialogService.getModal();
  return modal.error({
    ...defaultConfig,
    title,
    content,
    okText,
    onOk,
    onCancel,
    closable: closable ?? defaultConfig.closable,
    centered: centered ?? defaultConfig.centered,
  });
}

/**
 * Shows an info dialog for displaying informational messages
 * @param options - Dialog configuration options
 * @returns Modal instance for programmatic control
 *
 * @example
 * showInfo({
 *   title: 'Information',
 *   content: 'Your changes have been saved successfully.',
 * });
 */
export function showInfo(options: DialogOptions = {}) {
  const {
    title = defaultMessages.info.title,
    content = defaultMessages.info.content,
    okText = 'OK',
    onOk,
    onCancel,
    closable,
    centered,
  } = options;

  const modal = DialogService.getModal();
  return modal.info({
    ...defaultConfig,
    title,
    content,
    okText,
    onOk,
    onCancel,
    closable: closable ?? defaultConfig.closable,
    centered: centered ?? defaultConfig.centered,
  });
}

/**
 * Shows a warning dialog for displaying warning messages
 * @param options - Dialog configuration options
 * @returns Modal instance for programmatic control
 *
 * @example
 * showWarning({
 *   title: 'Warning',
 *   content: 'Please select at least one item before proceeding.',
 * });
 */
export function showWarning(options: DialogOptions = {}) {
  const {
    title = defaultMessages.warning.title,
    content = defaultMessages.warning.content,
    okText = 'OK',
    onOk,
    onCancel,
    closable,
    centered,
  } = options;

  const modal = DialogService.getModal();
  return modal.warning({
    ...defaultConfig,
    title,
    content,
    okText,
    onOk,
    onCancel,
    closable: closable ?? defaultConfig.closable,
    centered: centered ?? defaultConfig.centered,
  });
}

/**
 * Shows a confirm dialog for user confirmation actions
 * @param options - Confirm dialog configuration options
 * @returns Modal instance for programmatic control
 *
 * @example
 * showConfirm({
 *   title: 'Confirm Delete',
 *   content: 'Are you sure you want to delete this item?',
 *   okText: 'Delete',
 *   cancelText: 'Cancel',
 *   danger: true,
 *   onOk: async () => {
 *     await deleteItem(id);
 *   },
 *   onCancel: () => console.log('Cancelled'),
 * });
 */
export function showConfirm(options: ConfirmDialogOptions = {}) {
  const {
    title = defaultMessages.confirm.title,
    content = defaultMessages.confirm.content,
    okText = 'Confirm',
    cancelText = 'Cancel',
    okType = 'primary',
    danger = false,
    onOk,
    onCancel,
    closable,
    centered,
  } = options;

  const modal = DialogService.getModal();
  return modal.confirm({
    ...defaultConfig,
    title,
    content,
    okText,
    cancelText,
    okType,
    okButtonProps: danger ? { danger: true } : undefined,
    onOk,
    onCancel,
    closable: closable ?? defaultConfig.closable,
    centered: centered ?? defaultConfig.centered,
  });
}

/**
 * Shows a delete confirmation dialog with danger styling
 * Convenience method for common delete confirmation use case
 * @param options - Dialog options, title defaults to 'Confirm Delete'
 * @returns Modal instance for programmatic control
 *
 * @example
 * showDeleteConfirm({
 *   content: 'Are you sure you want to delete 3 users?',
 *   onOk: async () => {
 *     await deleteUsers(selectedIds);
 *   },
 * });
 */
export function showDeleteConfirm(
  options: Omit<ConfirmDialogOptions, 'danger' | 'okType'> & {
    title?: ReactNode;
  } = {},
) {
  const {
    title = defaultMessages.deleteConfirm.title,
    content = defaultMessages.deleteConfirm.content,
    okText = 'Delete',
    ...restOptions
  } = options;
  return showConfirm({
    content,
    ...restOptions,
    title,
    okText,
    danger: true,
    okType: 'primary',
  });
}

/**
 * Shows a success dialog for displaying success messages
 * @param options - Dialog configuration options
 * @returns Modal instance for programmatic control
 *
 * @example
 * showSuccess({
 *   title: 'Success',
 *   content: 'Data has been saved successfully.',
 * });
 */
export function showSuccess(options: DialogOptions = {}) {
  const {
    title = defaultMessages.success.title,
    content = defaultMessages.success.content,
    okText = 'OK',
    onOk,
    onCancel,
    closable,
    centered,
  } = options;

  const modal = DialogService.getModal();
  return modal.success({
    ...defaultConfig,
    title,
    content,
    okText,
    onOk,
    onCancel,
    closable: closable ?? defaultConfig.closable,
    centered: centered ?? defaultConfig.centered,
  });
}

/**
 * Dialog utility object for convenient access to all dialog methods
 *
 * @example
 * import { dialog } from '@/shared/utils/dialog';
 *
 * // Show error
 * dialog.error({ title: 'Error', content: 'Something went wrong' });
 *
 * // Show info
 * dialog.info({ title: 'Info', content: 'Operation completed' });
 *
 * // Show warning
 * dialog.warning({ title: 'Warning', content: 'Please select an item' });
 *
 * // Show confirm
 * dialog.confirm({
 *   title: 'Confirm',
 *   content: 'Are you sure?',
 *   onOk: () => doSomething(),
 * });
 *
 * // Show delete confirm
 * dialog.deleteConfirm({
 *   content: 'Delete this record?',
 *   onOk: () => deleteRecord(),
 * });
 *
 * // Show success
 * dialog.success({ title: 'Success', content: 'Operation successful' });
 */
/**
 * Custom hook for initializing dialog service using Ant Design App component
 *
 * IMPORTANT: Must be called within App component context (inside AntApp wrapper)
 *
 * @example
 * function AppContent() {
 *   useDialogService(); // Initialize dialog service
 *   // ... rest of app
 * }
 */
export const useDialogService = () => {
  const { modal } = App.useApp();

  // Initialize the dialog service instance
  DialogService.setInstance(modal);
};

export const dialog = {
  error: showError,
  info: showInfo,
  warning: showWarning,
  confirm: showConfirm,
  deleteConfirm: showDeleteConfirm,
  success: showSuccess,
} as const;

export default dialog;
